// Deterministic "pick 3 cards" logic for the Overwhelm flow.
//
// The spec describes a *deterministic* selection — we intentionally do NOT
// route this through Claude. Latency matters most during a panic moment;
// adding a 1-3s LLM call would betray the entire purpose of the feature.
//
// Order in the returned array matches the spec's card order:
//   [easy win 🌱, one step 🎯, just for you 🌸]
// If fewer than 3 candidates exist, returns fewer cards. No empty slots.
export function pickOverwhelmCards({ tasks = [], habits = [], todayKey }) {
    const undoneTasks = tasks.filter((t) => !t.completed);

    // ---- Card 2 (one step) — high priority OR overdue ------------------
    // Picked first because it's the most-constrained selector. We bias to
    // high-priority over overdue to keep the "meaningful" intent intact.
    const overdue = (t) => t.date && todayKey && t.date < todayKey;
    const card2Source =
        undoneTasks.find((t) => t.priority === "high")
        || undoneTasks.find((t) => overdue(t))
        || undoneTasks.find((t) => t.priority === "medium")
        || null;

    // ---- Card 1 (easy win) — lowest activation energy ------------------
    // Preference order: low priority → no due_time → anything left.
    const usedIds = new Set([card2Source?.id].filter(Boolean));
    const remaining1 = undoneTasks.filter((t) => !usedIds.has(t.id));
    const card1Source =
        remaining1.find((t) => t.priority === "low")
        || remaining1.find((t) => !t.due_time)
        || remaining1[0]
        || null;

    // ---- Card 3 (just for you) — habit if any, else gentlest task ------
    const undoneHabits = (habits || []).filter(
        (h) => !((h.completed_dates || []).includes(todayKey))
    );
    if (card1Source) usedIds.add(card1Source.id);

    let card3 = null;
    if (undoneHabits.length > 0) {
        const h = undoneHabits[0];
        card3 = {
            id: `habit:${h.id}`,
            sourceId: h.id,
            kind: "habit",
            title: h.title,
        };
    } else {
        const remaining3 = undoneTasks.filter((t) => !usedIds.has(t.id));
        const c3 =
            remaining3.find((t) => t.priority === "low")
            || remaining3.find((t) => !t.due_time)
            || remaining3[0]
            || null;
        if (c3) {
            card3 = { id: c3.id, sourceId: c3.id, kind: "task", title: c3.title };
        }
    }

    const out = [];
    if (card1Source) {
        out.push({
            id: card1Source.id, sourceId: card1Source.id, kind: "task",
            title: card1Source.title, label: "easy win", emoji: "🌱",
        });
    }
    if (card2Source) {
        out.push({
            id: card2Source.id, sourceId: card2Source.id, kind: "task",
            title: card2Source.title, label: "one step", emoji: "🎯",
        });
    }
    if (card3) {
        out.push({ ...card3, label: "just for you", emoji: "🌸" });
    }
    return out;
}

// Speech bubble copy — rotates so we never repeat back-to-back.
const OVERWHELM_OPENERS = [
    "hey. let's just do one small thing. which of these feels okay?",
    "it's okay. we don't have to do everything. just one.",
    "i've got you. pick one and we'll do it together.",
    "the list can wait. you just need one thing right now.",
    "breathe. here are three small options.",
];
const LAST_OPENER_KEY = "mochi.lastOverwhelmOpener.v1";
export function pickOverwhelmOpener() {
    let last = "";
    try { last = localStorage.getItem(LAST_OPENER_KEY) || ""; } catch { /* ignore */ }
    const candidates = OVERWHELM_OPENERS.filter((l) => l !== last);
    const pool = candidates.length > 0 ? candidates : OVERWHELM_OPENERS;
    const line = pool[Math.floor(Math.random() * pool.length)];
    try { localStorage.setItem(LAST_OPENER_KEY, line); } catch { /* ignore */ }
    return line;
}
